import { useCallback, useState } from "react";

import { ParentLayout } from "../components/ParentLayout";
import { SectionLayout } from "../components/SectionLayout";
import { MainLayout } from "../components/MainLayout";
import { SectionItem } from "../components/SectionItem";

import AssistantCoachPrompt from "./AssistantCoachPrompt";
import AssistantCoachKnowledge from "./AssistantCoachKnowledgeBase";
import AssistantCoachEnable from "./AssistantCoachEnable";
import AssistantCoachModel from "./AssistantCoachModel";
import {
  BoltIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  DocumentDuplicateIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import { updateAssistantCoachSettings } from "../../service";
import AssistantCoachTrigger from "./AssistantCoachTrigger";
import AssistantCoachExecution from "./AssistantCoachExecution";

export default AssistantCoach;

enum AssistantCoachTabs {
  Prompt = "Prompt",
  Model = "Model",
  Triggers = "Triggers",
  KnowledgeBase = "Knowledge Base",
  Execution_Settings = "Execution Settings",
}

const AssistantCoachTabIconRegistry = {
  [AssistantCoachTabs.Prompt]: DocumentDuplicateIcon,
  [AssistantCoachTabs.KnowledgeBase]: FolderIcon,
  [AssistantCoachTabs.Model]: CpuChipIcon,
  [AssistantCoachTabs.Triggers]: BoltIcon,
  [AssistantCoachTabs.Execution_Settings]: Cog6ToothIcon,
};

const AssistantCoachTabRegistry = {
  [AssistantCoachTabs.Prompt]: AssistantCoachPrompt,
  [AssistantCoachTabs.Model]: AssistantCoachModel,
  [AssistantCoachTabs.KnowledgeBase]: AssistantCoachKnowledge,
  [AssistantCoachTabs.Triggers]: AssistantCoachTrigger,
  [AssistantCoachTabs.Execution_Settings]: AssistantCoachExecution,
};

function AssistantCoach(props) {
  const {
    assistantCoach,
    syncAssistant,
    workspaceId,
    assistantId,
    locked,
  } = props;

  const [activeTab, setActiveTab] = useState<AssistantCoachTabs>(
    AssistantCoachTabs.Prompt,
  );

  const tabs = Object.values(AssistantCoachTabs).reduce((acc, tab) => {
    return [...acc, { name: tab, href: "#", current: tab === activeTab }];
  }, []);

  const onUpdateAssistant = useCallback(
    async (payload, lifecycleCbs) => {
      lifecycleCbs.init();

      try {
        const response = await updateAssistantCoachSettings({
          workspaceId,
          assistantId,
          payload,
        });

        if (response.status === 200) {
          const data = response.data;
          syncAssistant((prev) => {
            return {
              ...prev,
              assistantCoach: data,
            };
          });
          lifecycleCbs.success();
        }
      } catch (e) {
        lifecycleCbs.error(e);
      }
    },
    [workspaceId, assistantId],
  );

  const isUnlocked = !locked && (assistantCoach?.coachEnabled ?? false);

  const AssistantCoachTabComponent = AssistantCoachTabRegistry[activeTab];

  return (
    <div className="p-4 h-full">
      <div className="flex flex-col flex-1 space-y-4 h-full min-h-0">
        <AssistantCoachEnable
          coachEnabled={assistantCoach?.coachEnabled}
          onUpdateAssistant={onUpdateAssistant}
          locked={locked}
        />

        <div className="flex flex-1 flex-col overflow-hidden -px-2">
          <ParentLayout>
            <SectionLayout>
              {tabs.map((tab) => {
                const Icon = AssistantCoachTabIconRegistry[tab.name];

                return (
                  <SectionItem
                    sectionName={tab.name}
                    isActive={tab.current}
                    icon={<Icon className="size-4" />}
                    onSectionClick={() => setActiveTab(tab.name)}
                  />
                );
              })}
            </SectionLayout>
            <MainLayout>
              <AssistantCoachTabComponent
                {...props}
                onUpdateAssistant={onUpdateAssistant}
                isUnlocked={isUnlocked}
              />
            </MainLayout>
          </ParentLayout>
        </div>
      </div>
    </div>
  );
}
